import React, { useEffect } from 'react';
import { Users, Calendar, BookOpen, MessageSquare, LogOut, X } from 'lucide-react';
import { User } from '../types';
import { IconButton } from './ui';

type Tab = 'crm' | 'contacts' | 'bookings' | 'calendar';

interface SidebarProps {
  activeTab: Tab;
  setActiveTab: (tab: Tab) => void;
  user: User;
  onLogout: () => void;
  isMobileOpen: boolean;
  onCloseMobile: () => void;
}

const items: Array<{
  id: Tab;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
}> = [
  { id: 'crm', label: 'Customers', Icon: Users },
  { id: 'calendar', label: 'Calendar', Icon: Calendar },
  { id: 'bookings', label: 'Bookings', Icon: BookOpen },
  { id: 'contacts', label: 'Communications', Icon: MessageSquare },
];

const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  setActiveTab,
  user,
  onLogout,
  isMobileOpen,
  onCloseMobile,
}) => {
  // Close mobile drawer on Escape
  useEffect(() => {
    if (!isMobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseMobile();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobileOpen, onCloseMobile]);

  const handleSelect = (tab: Tab) => {
    setActiveTab(tab);
    onCloseMobile();
  };

  return (
    <>
      {/* Mobile backdrop */}
      <div
        aria-hidden="true"
        onClick={onCloseMobile}
        className={[
          'fixed inset-0 bg-sand-900/40 z-40 md:hidden transition-opacity',
          isMobileOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none',
        ].join(' ')}
      />

      <aside
        className={[
          'fixed md:static inset-y-0 left-0 z-50 w-64 md:w-60',
          'bg-white border-r border-sand-200 flex flex-col shrink-0',
          'transform transition-transform md:transform-none',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        ].join(' ')}
      >
        <div className="h-16 flex items-center px-5 border-b border-sand-200 justify-between">
          <div className="flex items-center">
            <span className="w-8 h-8 rounded-xl bg-sage-600 text-white inline-flex items-center justify-center font-display text-base mr-3">
              O
            </span>
            <div className="leading-tight">
              <p className="text-sand-900 font-display text-base font-medium">
                Otto's Barn
              </p>
              <p className="text-[11px] text-sand-500">Holiday let CRM</p>
            </div>
          </div>
          <IconButton
            label="Close menu"
            onClick={onCloseMobile}
            className="md:hidden"
          >
            <X className="h-4 w-4" />
          </IconButton>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {items.map(({ id, label, Icon }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => handleSelect(id)}
                className={[
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500/40',
                  active
                    ? 'bg-sage-50 text-sage-800 font-medium'
                    : 'text-sand-600 hover:text-sand-900 hover:bg-sand-50',
                ].join(' ')}
              >
                <Icon
                  className={[
                    'h-4 w-4 shrink-0',
                    active ? 'text-sage-600' : 'text-sand-500',
                  ].join(' ')}
                />
                <span>{label}</span>
              </button>
            );
          })}
        </nav>

        <div className="px-3 pb-3 pt-2 border-t border-sand-200">
          <div className="px-3 pt-3 pb-2">
            <p className="text-sm font-medium text-sand-900 truncate">
              {user.username}
            </p>
            <p className="text-xs text-sand-500 capitalize">{user.role}</p>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-sand-600 hover:text-red-700 hover:bg-red-50 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
